import "@app/App.css";
import { Button } from "@comps/ui/button";
import CompozzSpinner from "@comps/ui/CompozzSpinner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { DuplicateRecordRequest } from "@dto/records";
import { AddRightOption, EditRightAccess, hasGlobalRightAccess, hasGlobalRightOption, ObjectRightType, ViewRightType } from "@dto/Users";
import { BASE_URL } from "@utils/constants";
import { getRecordKeys } from "@utils/data";
import { toastError, toastSuccess } from "@utils/toasts";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ObjectHeaderButtonTitle, TW_ObjectHeaderButtonTitleDisabled, TW_ObjectModelViewHeaderButton, TW_ObjectModelViewHeaderButtonDisabled, TW_ObjectModelViewHeaderIcon, TW_ObjectModelViewHeaderIconDisabled, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { getUndoSessionId } from "@utils/session";
import { UndoLabelCode, useUndoStore } from "@utils/undo";
import { Copy } from "iconsax-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore, useUserStore } from "src/store";


function DuplicateRowsButton() {

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const workspace = useModelStore(state => state.selectedWorkspace);
  const user = useUserStore(state => state.user);
  const { t } = useTranslation();
  const rowSelection = useDataStore((state) => state.rowSelection);
  const setRowSelection = useDataStore((state) => state.setRowSelection);
  const metaLines = useDataStore((state) => state.metaLines);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const fetchData = useDataStore(state => state.fetchData);
  const loadData = useDataStore(state => state.loadData);
  const stopLoadingData = useDataStore(state => state.stopLoadingData);
  const userRole = useModelStore(state => state.userRole);
  const selectedView = useModelStore(state => state.selectedView);
  const loadingData = useDataStore(state => state.loadingData);
  const rowActionsEnabled = useDataStore(state => state.rowActionsEnabled);

  const fields = selectedObjectInfos?.fields;
  const numberOfSelection = Object.entries(rowSelection).length
  var plurialStr = ""
  if (numberOfSelection > 1) {
    plurialStr = "s"
  }

  function cancel() {
    setIsDialogOpen(false);
  }

  function onSubmit() {
    if (!workspace || !selectedObjectInfos?.object) {
      console.error("No workspace selected");
      return;
    }

    loadData();
    const recordKeys: string[] = [];
    Object.keys(rowSelection).forEach(key => {
      const metaLine = metaLines.get(key);
      if (metaLine && metaLine.line.items.length > 0) {
        const lineRecordKeys = getRecordKeys(metaLine, selectedObjectInfos.object._key);
        if (lineRecordKeys.length > 0) {
          recordKeys.push(...lineRecordKeys);
        }
      }
    });


    const request: DuplicateRecordRequest = {
      workspaceKey: workspace._key,
      objectKey: selectedObjectInfos.object._key,
      recordKeys: recordKeys,
      overridenFields: new Map(),
      overridenLinks: new Map(),
      sessionId: getUndoSessionId(),
    }

    post(`${BASE_URL}/data/records/duplicate`, request).then(response => {
      // No response means success
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            toastError(translate(t, cError.i18n, cError.params));
          } else {
            toastError(cError.message);
          }
        } else {
          toastSuccess(t("record.duplicate.success"));
          setRowSelection({});
          useUndoStore.getState().markUndoable(selectedObjectInfos.object._key, UndoLabelCode.duplicate, recordKeys.length);
        }
      } else {
        toastError(t("record.duplicate.error"));
      }
      fetchData();
    })
      .finally(() => {
        stopLoadingData();
        setIsDialogOpen(false);
      });

  }

  const hasEditAccessOnObject = hasGlobalRightAccess(user, userRole, ObjectRightType, EditRightAccess, selectedObjectInfos?.object?._key);
  const hasEditAccessOnView = hasGlobalRightAccess(user, userRole, ViewRightType, EditRightAccess, selectedView?._key);
  const hasEditAccess = hasEditAccessOnObject && hasEditAccessOnView;
  // Duplicating creates records: the back-end requires the ADD option
  const hasAddAccessOnObject = hasGlobalRightOption(user, userRole, ObjectRightType, AddRightOption, selectedObjectInfos?.object?._key);
  const hasAddAccessOnView = hasGlobalRightOption(user, userRole, ViewRightType, AddRightOption, selectedView?._key);
  const hasAddAccess = hasEditAccess && hasAddAccessOnObject && hasAddAccessOnView;
  const disabled = !fields || !hasAddAccess || numberOfSelection === 0 || !rowActionsEnabled;

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger disabled={disabled} className={disabled ? TW_ObjectModelViewHeaderButtonDisabled : TW_ObjectModelViewHeaderButton}>
        {disabled && <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger className="flex flex-row justify-start items-center relative gap-2">
              <div className="flex flex-row justify-start items-center relative">
                <Copy className={TW_ObjectModelViewHeaderIconDisabled} />
              </div>
              <span className={TW_ObjectHeaderButtonTitleDisabled}>{t('object.header.button.duplicate.rows.title')}</span>
            </TooltipTrigger>
            <TooltipContent>
              {(rowActionsEnabled ? t('object.header.button.duplicate.rows.forbidden.tooltip') : t('object.header.button.duplicate.rows.actions.disabled.tooltip'))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>}
        {!disabled &&
          <>
            <div className="flex flex-row justify-start items-center relative">
              <Copy className={TW_ObjectModelViewHeaderIcon} />
            </div>
            <span className={TW_ObjectHeaderButtonTitle}>{t('object.header.button.duplicate.rows.title')}</span>
          </>}
      </DialogTrigger>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="space-y-2">
          <DialogTitle>{t("object.header.dialog.duplicate.rows.title")}</DialogTitle>
          <DialogDescription hidden={loadingData}>{translate(t, "object.header.dialog.duplicate.rows.description", [numberOfSelection, plurialStr])}</DialogDescription>
        </DialogHeader>
        {loadingData && <div className="flex flex-col justify-center items-center h-full gap-4">
          <p className="text-sm">
            {t("record.duplicate.loading")}
          </p>
          <CompozzSpinner />
        </div>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton}  onClick={cancel} {...cancelHover} disabled={loadingData}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} onClick={onSubmit} {...okHover} disabled={loadingData}>{t("general.button.ok.text")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DuplicateRowsButton;
